import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Mail,
  FileSpreadsheet,
  MessageSquare,
  CreditCard,
  FileText,
  ShieldCheck,
  CheckCircle2,
  TrendingUp,
  Clock,
  ArrowRight,
  Sparkles,
  Download,
  Printer,
  ChevronDown,
  HelpCircle,
  Scale,
  Users,
  Check,
  Zap,
  Lock,
  ExternalLink,
  PhoneCall,
  Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { CarteraPhBrochurePdfService } from "@/services/carteraPhBrochurePdfService";
import CarteraPhInteractiveDemo from "@/components/cartera-ph/CarteraPhInteractiveDemo";
import { toast } from "sonner";

// Tarifas base de suscripción mensual por copropiedad (1 Copropiedad)
const BASE_PRICES = {
  pequena: 350000, // Hasta 80 unidades privadas
  mediana: 650000, // 81 a 200 unidades privadas
  grande: 1150000  // Más de 200 unidades privadas
};

// Escala de descuento por volumen multicopropiedad
const getDiscountByCount = (count: number) => {
  if (count >= 7) return 0.35; // 35% de descuento por copropiedad
  if (count >= 4) return 0.25; // 25% de descuento por copropiedad
  if (count >= 2) return 0.15; // 15% de descuento por copropiedad
  return 0;
};

export default function CarteraPHLanding() {
  const [apartamentos, setApartamentos] = useState<number>(120);
  const [cuotaAdmin, setCuotaAdmin] = useState<number>(320000);
  const [numCopropiedadesCalc, setNumCopropiedadesCalc] = useState<number>(1);
  const [copropiedadesPlan, setCopropiedadesPlan] = useState<number>(1);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isDownloadingBrochure, setIsDownloadingBrochure] = useState(false);

  const handleDownloadBrochure = () => {
    try {
      setIsDownloadingBrochure(true);
      toast.info('Generando Brochure comercial en PDF...');
      const doc = CarteraPhBrochurePdfService.generateBrochurePdf();
      doc.save('Brochure_Comercial_MiA_Cartera_PH.pdf');
      toast.success('Brochure descargado exitosamente.');
    } catch (e) {
      console.error(e);
      toast.error('Error generando brochure PDF.');
    } finally {
      setIsDownloadingBrochure(false);
    }
  };

  // Precios dinámicos de los planes según cantidad de copropiedades
  const planPrices = useMemo(() => {
    const discount = getDiscountByCount(copropiedadesPlan);
    const discountPercent = Math.round(discount * 100);

    const calcPlan = (basePrice: number) => {
      const pricePerPh = Math.round(basePrice * (1 - discount));
      const totalMonthly = pricePerPh * copropiedadesPlan;
      const savingsPerPh = basePrice - pricePerPh;
      const totalSavings = savingsPerPh * copropiedadesPlan;

      return {
        basePrice,
        pricePerPh,
        totalMonthly,
        savingsPerPh,
        totalSavings,
        discountPercent
      };
    };

    return {
      pequena: calcPlan(BASE_PRICES.pequena),
      mediana: calcPlan(BASE_PRICES.mediana),
      grande: calcPlan(BASE_PRICES.grande),
      discountPercent
    };
  }, [copropiedadesPlan]);

  const stats = useMemo(() => {
    const morososRecuperados = Math.max(1, Math.round(apartamentos * 0.15));
    const recaudoTotal = morososRecuperados * cuotaAdmin * numCopropiedadesCalc;

    let tarifaBasePorPh = BASE_PRICES.pequena;
    if (apartamentos > 80 && apartamentos <= 200) tarifaBasePorPh = BASE_PRICES.mediana;
    if (apartamentos > 200) tarifaBasePorPh = BASE_PRICES.grande;

    const discount = getDiscountByCount(numCopropiedadesCalc);
    const tarifaPorCopropiedad = Math.round(tarifaBasePorPh * (1 - discount));
    const tarifaSaaSTotal = tarifaPorCopropiedad * numCopropiedadesCalc;
    const roiRatio = Math.max(1, Math.round(recaudoTotal / tarifaSaaSTotal));

    return {
      morososRecuperados: morososRecuperados * numCopropiedadesCalc,
      recaudoTotal,
      tarifaPorCopropiedad,
      tarifaSaaS: tarifaSaaSTotal,
      roiRatio,
      costoUnitario: Math.round(tarifaPorCopropiedad / apartamentos),
      numCopropiedades: numCopropiedadesCalc,
      discountPercent: Math.round(discount * 100)
    };
}, [apartamentos, cuotaAdmin, numCopropiedadesCalc]);

const faqs = [
  {
    q: "¿Ya tenemos software contable (Daytona, SISCO, Siigo, WordOffice). ¿Esto lo reemplaza?",
    a: "No lo reemplaza, lo potencia. Su software contable registra números contables, pero no realiza cobranza activa con inteligencia jurídica ni envía requerimientos por WhatsApp con botón de pago PSE directo ni blinda ante la SIC. Usted solo exporta el Excel de cartera de su sistema contable actual, lo sube a MiA en 1 clic y el sistema se encarga de recuperar el dinero."
  },
  {
    q: "¿Cobrar por WhatsApp infringe la Ley 2300 de 2023 ('Dejen de Fregar')?",
    a: "Al contrario: el cobro manual e informal que hacen muchos administradores hoy sí expone a la copropiedad a multas de la SIC. MiA tiene un motor de cumplimiento algorítmico que bloquea automáticamente envíos en fines de semana, festivos y fuera de horas hábiles, y restringe el contacto a los límites legales guardando una bitácora inmutable auditable."
  },
  {
    q: "¿Esto reemplaza al abogado de cobro judicial de la copropiedad?",
    a: "No lo reemplaza; hace su trabajo 10 veces más rápido y le ahorra plata a la copropiedad. El 70% de la cartera morosa se recupera en la etapa preventiva y prejurídica sin pagar honorarios del 20% al 30%. Y para el 30% restante que deba ir a juzgado, MiA le entrega al abogado el Título Ejecutivo en PDF (Ley 675 Art. 48) perfectamente liquidado con intereses legales listo para radicar embargo."
  },
  {
    q: "¿Cómo paga el copropietario cuando recibe la notificación?",
    a: "Cada notificación por WhatsApp o correo incluye un botón y enlace corto exclusivo para su inmueble. Al hacer clic, se abre una pasarela oficial segura de PSE o tarjeta de crédito. En menos de 30 segundos su banco debita el saldo y el dinero entra directo a la cuenta bancaria del conjunto residencial."
  }
];

return (
  <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col selection:bg-emerald-100 selection:text-emerald-900">
    <SEO
      title="Recuperación de Cartera en Propiedad Horizontal | MiA Legal PH"
      description="Plataforma de cobro prejurídico automatizado para conjuntos residenciales en Colombia. Notificaciones por WhatsApp con botón PSE, Título Ejecutivo Art. 48 Ley 675 y blindaje Ley 2300."
    />

    <Header />

    <main className="flex-1">
      {/* HERO SECTION (LIGHT THEME) */}
      <section className="relative overflow-hidden pt-12 pb-20 md:pt-20 md:pb-28 border-b border-slate-200/80 bg-gradient-to-b from-white via-slate-50 to-slate-100/60">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(16,185,129,0.08),rgba(255,255,255,0))] pointer-events-none" />

        <div className="container mx-auto px-4 max-w-6xl relative z-10 text-center">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs md:text-sm font-semibold tracking-wide uppercase mb-6 shadow-sm">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            Solución LegalTech para Administradores de PH en Colombia
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl mx-auto leading-tight md:leading-tight mb-6">
            Recupera la Cartera de tu Conjunto en{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600">
              3 Pasos y con 1 Clic
            </span>
          </h1>

          <p className="text-base md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Cobranza prejurídica con <strong>botón directo de pago PSE en WhatsApp</strong>, liquidación
            de intereses bajo la <strong>Ley 675 de 2001</strong> y blindaje 100% automático contra sanciones
            de la <strong>Ley 2300</strong> ("Dejen de Fregar").
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto mb-14">
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold shadow-xs gap-2"
              asChild
            >
              <a href="#demo-interactiva">
                <Play className="h-4 w-4 text-emerald-700 fill-emerald-700" />
                Simulador & Demo Guiada
              </a>
            </Button>

            <Button
              size="lg"
              className="w-full sm:w-auto bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-6 shadow-xs gap-2"
              asChild
            >
              <a href="#calculadora">
                <TrendingUp className="h-4 w-4" />
                Calcular Ahorro y ROI
              </a>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold shadow-xs gap-2"
              asChild
            >
              <Link to="/brochure-cartera-ph">
                <FileText className="h-4 w-4 text-slate-600" />
                Ver Brochure Comercial
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shadow-xs gap-2"
              onClick={handleDownloadBrochure}
              disabled={isDownloadingBrochure}
            >
              <Download className="h-4 w-4 text-slate-600" />
              {isDownloadingBrochure ? "Descargando..." : "Descargar PDF"}
            </Button>
          </div>

          {/* Quick Metrics Bar (Light) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl md:text-3xl font-extrabold text-emerald-700">+45%</div>
              <div className="text-xs md:text-sm font-medium text-slate-600 mt-1">Recaudo en 15 Días</div>
            </div>
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl md:text-3xl font-extrabold text-slate-800">30 Seg</div>
              <div className="text-xs md:text-sm font-medium text-slate-600 mt-1">Tiempo de Pago vía PSE</div>
            </div>
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl md:text-3xl font-extrabold text-slate-800">1 Clic</div>
              <div className="text-xs md:text-sm font-medium text-slate-600 mt-1">Título Ejecutivo Ley 675</div>
            </div>
            <div className="p-5 rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className="text-2xl md:text-3xl font-extrabold text-emerald-700">100%</div>
              <div className="text-xs md:text-sm font-medium text-slate-600 mt-1">Blindado Ley 2300 (SIC)</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECCIÓN DEMO INTERACTIVA / VIDEO TOUR GUIADO (LIGHT & SOBRIO) ── */}
      <section id="demo-interactiva" className="py-20 bg-slate-50 text-slate-900 border-b border-slate-200/80 scroll-mt-10">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white mb-3 uppercase tracking-wider text-xs font-medium py-1 px-3">
              Simulador Interactivo de Cobranza
            </Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              Vea Cómo Opera MiA Cartera PH en Tiempo Real
            </h2>
            <p className="text-slate-600 mt-3 text-base md:text-lg">
              Explore el flujo de recuperación de cartera: interactúe con el panel del administrador y pruebe el botón de pago PSE en la pantalla simulada del copropietario.
            </p>
          </div>

          <CarteraPhInteractiveDemo />
        </div>
      </section>

      {/* ── SECCIÓN INFOGRAFÍA: EL FLUJO EN 3 PASOS (LIGHT) ── */}
      <section className="py-20 border-b border-slate-200/80 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-sky-50 text-sky-800 border-sky-200 mb-3 uppercase tracking-wider text-xs font-semibold">
              Flujo Operativo Simple
            </Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              ¿Cómo Funciona la Recuperación de Cartera?
            </h2>
            <p className="text-slate-600 mt-3 text-base md:text-lg">
              Diseñado para la realidad diaria del Administrador: sin digitar nada a mano, sin intermediarios y con respaldo judicial.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Paso 1 */}
            <div className="relative group rounded-2xl border border-slate-200 bg-slate-50/60 p-8 hover:bg-white hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-600/5 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-800 font-extrabold text-lg mb-6 group-hover:scale-110 transition-transform shadow-sm">
                1
              </div>
              <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                <h3 className="text-xl font-bold text-slate-900">Sube tu Excel</h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Exporta la planilla de morosos de tu software contable actual (<strong>Daytona, SISCO, Siigo, WordOffice</strong>) y súbela en un solo clic.
              </p>
              <div className="text-xs text-emerald-800 font-medium bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                ✓ El sistema clasifica deudas, apartamentos y días de mora automáticamente.
              </div>
            </div>

            {/* Paso 2 */}
            <div className="relative group rounded-2xl border border-slate-200 bg-slate-50/60 p-8 hover:bg-white hover:border-sky-300 hover:shadow-xl hover:shadow-sky-600/5 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center text-sky-800 font-extrabold text-lg mb-6 group-hover:scale-110 transition-transform shadow-sm">
                2
              </div>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-5 w-5 text-sky-600" />
                <h3 className="text-xl font-bold text-slate-900">Cobro WhatsApp + PSE</h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                El residente moroso recibe en su celular un requerimiento respetuoso y formal con un <strong>botón oficial de pago PSE</strong>.
              </p>
              <div className="text-xs text-sky-800 font-medium bg-sky-50 border border-sky-200 rounded-lg p-2.5">
                ✓ Paga en 30 segundos desde su banco. Se acaba la excusa de "no tengo la cuenta".
              </div>
            </div>

            {/* Paso 3 */}
            <div className="relative group rounded-2xl border border-slate-200 bg-slate-50/60 p-8 hover:bg-white hover:border-amber-300 hover:shadow-xl hover:shadow-amber-600/5 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-800 font-extrabold text-lg mb-6 group-hover:scale-110 transition-transform shadow-sm">
                3
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Scale className="h-5 w-5 text-amber-600" />
                <h3 className="text-xl font-bold text-slate-900">Recaudo o Título PDF</h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Si no paga en el plazo, emites en 1 clic el <strong>Título Ejecutivo Certificado (Ley 675 Art. 48)</strong> con mérito ejecutivo para demanda civil.
              </p>
              <div className="text-xs text-amber-800 font-medium bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                ✓ Listo para que el abogado radique solicitud de embargo sin peritajes costosos.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5 PILARES DE DIFERENCIACIÓN (LIGHT) ── */}
      <section className="py-20 border-b border-slate-200/80 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-3 uppercase tracking-wider text-xs font-semibold">
              Tecnología Exclusiva
            </Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              Los 5 Pilares de Diferenciación Tecnológica
            </h2>
            <p className="text-slate-600 mt-3 text-base md:text-lg">
              Lo que ninguna casa de cobranza ni software contable ofrece hoy en Colombia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-2">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <CardTitle className="text-lg text-slate-900">1. Carga Masiva Inteligente</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>Mapeo algorítmico de torres, apartamentos, propietarios y deudas acumuladas en menos de 3 segundos.</p>
                <div className="text-xs text-emerald-700 font-semibold pt-2">Ahorro de hasta 15 horas mensuales de digitación.</div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-600 mb-2">
                  <CreditCard className="h-5 w-5" />
                </div>
                <CardTitle className="text-lg text-slate-900">2. Botón PSE/Wompi en WhatsApp</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>Enlace dinámico y seguro por cada unidad privada. El deudor entra directo a pagar sin digitar números de cuenta.</p>
                <div className="text-xs text-sky-700 font-semibold pt-2">Aumento del +45% en la velocidad de pago.</div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mb-2">
                  <Clock className="h-5 w-5" />
                </div>
                <CardTitle className="text-lg text-slate-900">3. Semáforo Legal de Mora</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>Separación en 3 etapas: Preventiva (1-30d), Prejurídica (31-60d) y Prejudicial con aviso de embargo (&gt;60d).</p>
                <div className="text-xs text-amber-700 font-semibold pt-2">Tono legal persuasivo sin generar conflicto vecinal.</div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600 mb-2">
                  <FileText className="h-5 w-5" />
                </div>
                <CardTitle className="text-lg text-slate-900">4. Título Ejecutivo en 1 Clic</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>Emisión inmediata del certificado del Art. 48 Ley 675 con liquidación discriminada de capital e intereses legales.</p>
                <div className="text-xs text-purple-700 font-semibold pt-2">Mérito ejecutivo pleno ante jueces civiles.</div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-1">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 mb-2">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <CardTitle className="text-lg text-slate-900">5. Blindaje Ley 2300 de 2023 (SIC)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>Filtro estricto de horarios hábiles y límites de contacto con bitácora inmutable auditable para asambleas.</p>
                <div className="text-xs text-rose-700 font-semibold pt-2">Cero riesgo de sanciones millonarias de la SIC.</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 flex flex-col justify-center p-6 text-center shadow-sm">
              <div className="text-xl font-bold text-slate-900 mb-2">Directorio Integrado</div>
              <p className="text-xs text-slate-600 mb-4">
                Base de datos de propietarios, historial de acuerdos de pago y bitácora completa de cada gestión realizada.
              </p>
              <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50" asChild>
                <a href="#precios">Ver Tarifas Disponibles</a>
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* ── CALCULADORA INTERACTIVA DE ROI (LIGHT) ── */}
      <section id="calculadora" className="py-20 border-b border-slate-200/80 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-3 uppercase tracking-wider text-xs font-semibold">
              Simulador Financiero en Tiempo Real
            </Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              ¿Cuánto Dinero Recuperará tu Copropiedad?
            </h2>
            <p className="text-slate-600 mt-3 text-base">
              Comprueba cómo el sistema se paga solo con recuperar una sola cuota de administración morosa.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-slate-50 border border-slate-200/90 rounded-3xl p-6 md:p-10 shadow-lg shadow-slate-200/50">
            {/* Sliders */}
              <div className="lg:col-span-7 space-y-6">
                <div>
                      <div className="flex justify-between items-center mb-2.5">
                        <span className="text-sm font-semibold text-slate-700">Copropiedades que Administras:</span>
                        <span className="text-base font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-md border border-emerald-200 font-mono">
                          {numCopropiedadesCalc} {numCopropiedadesCalc === 1 ? "Copropiedad" : "Copropiedades"}
                        </span>
                      </div>
                      <Slider
                        value={[numCopropiedadesCalc]}
                        min={1}
                        max={15}
                        step={1}
                        onValueChange={(val) => setNumCopropiedadesCalc(val[0])}
                        className="py-1.5"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1 font-mono">
                        <span>1 PH (Estándar)</span>
                        <span>5 PHs (-25% dto)</span>
                        <span>15 PHs (-35% dto)</span>
                      </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2.5">
                      <span className="text-sm font-semibold text-slate-700">Promedio Unidades por Conjunto:</span>
                      <span className="text-base font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-md border border-slate-200 font-mono">
                        {apartamentos} Unidades / PH
                      </span>
                    </div>
                    <Slider
                      value={[apartamentos]}
                      min={30}
                      max={500}
                      step={10}
                      onValueChange={(val) => setApartamentos(val[0])}
                      className="py-1.5"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1 font-mono">
                      <span>30 Unidades</span>
                      <span>250 Unidades</span>
                      <span>500 Unidades</span>
                    </div>
                  </div>

                  <div>
                      <div className="flex justify-between items-center mb-2.5">
                        <span className="text-sm font-semibold text-slate-700">Cuota Promedio de Administración:</span>
                          <span className="text-base font-bold text-sky-800 bg-sky-100/70 px-3 py-1 rounded-md border border-sky-200 font-mono">
                            ${cuotaAdmin.toLocaleString("es-CO")} COP
                          </span>
                      </div>
                      <Slider
                        value={[cuotaAdmin]}
                        min={150000}
                        max={1200000}
                        step={25000}
                        onValueChange={(val) => setCuotaAdmin(val[0])}
                        className="py-1.5"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1 font-mono">
                        <span>$150.000</span>
                        <span>$600.000</span>
                        <span>$1.200.000 COP</span>
                      </div>
                    </div>

                      <div className="p-3.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 leading-relaxed shadow-sm">
                        💡 <strong>Cálculo Conservador:</strong> Se asume una recuperación del 15% de unidades morosas durante el primer ciclo quincenal con el botón PSE/Wompi en WhatsApp.
                      </div>
                    </div>

                    {/* Resultado Card */}
                      <div className="lg:col-span-5 rounded-2xl bg-gradient-to-br from-emerald-50 via-teal-50 to-white border border-emerald-300 p-6 md:p-8 text-center space-y-4 shadow-sm">
                        <div className="text-xs uppercase font-bold tracking-wider text-emerald-800">
                          Recaudo Estimado Primer Mes
                        </div>
                        <div className="text-3xl md:text-4xl font-extrabold text-slate-900 font-mono">
                          ${stats.recaudoTotal.toLocaleString("es-CO")} <span className="text-sm text-slate-500">COP</span>
                        </div>
                        <p className="text-xs text-slate-600">
                          Correspondiente a ~{stats.morososRecuperados} cuotas recuperadas directamente a la cuenta de la copropiedad.
                        </p>

                            <div className="pt-4 border-t border-emerald-200/60 space-y-2 text-left">
                              <div className="flex justify-between text-xs text-slate-700">
                                <span>Valor por copropiedad:</span>
                                <span className="font-bold font-mono text-emerald-800">
                                  ${stats.tarifaPorCopropiedad.toLocaleString("es-CO")} COP/mes
                                </span>
                              </div>
                              {numCopropiedadesCalc > 1 && (
                                <div className="flex justify-between text-xs text-slate-600">
                                  <span>Total mensual ({numCopropiedadesCalc} PHs):</span>
                                  <span className="font-bold font-mono text-slate-900">
                                    ${stats.tarifaSaaS.toLocaleString("es-CO")} COP/mes
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between text-xs text-slate-600">
                                <span>Costo por apartamento:</span>
                                <span className="font-mono text-slate-600">~${stats.costoUnitario.toLocaleString("es-CO")} COP/mes</span>
                              </div>
                              {stats.discountPercent > 0 && (
                                <div className="text-[11px] text-emerald-800 font-semibold bg-emerald-100/70 p-1.5 rounded-lg text-center border border-emerald-200">
                                  ✓ Descuento por volumen aplicado: -{stats.discountPercent}% por copropiedad
                                </div>
                              )}
                            </div>

                            <div className="p-3 rounded-lg bg-emerald-600 text-white font-bold text-sm shadow-sm">
                              Retorno de Inversión: {stats.roiRatio}x el costo del sistema
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ── COMPARATIVA: TRADICIONAL VS MiA (LIGHT) ── */}
                    <section className="py-20 border-b border-slate-200/80 bg-slate-50">
                      <div className="container mx-auto px-4 max-w-5xl">
                        <div className="text-center max-w-3xl mx-auto mb-14">
                          <Badge className="bg-slate-200 text-slate-700 border-slate-300 mb-3 uppercase tracking-wider text-xs font-semibold">
                            Análisis Comparativo
                          </Badge>
                          <h2 className="text-3xl font-extrabold text-slate-900">
                            Cobro Tradicional vs. MiA Cartera PH
                          </h2>
                        </div>

                        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                          <table className="w-full text-left border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-100/70">
                                <th className="p-4 font-bold text-slate-700">Aspecto</th>
                                <th className="p-4 font-bold text-slate-600">Cobro Tradicional / Abogado Externo</th>
                                <th className="p-4 font-bold text-emerald-800 bg-emerald-50">Con MiA Cartera PH</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="p-4 font-semibold text-slate-900">Costo de Cobro</td>
                                <td className="p-4 text-slate-600">15% a 30% de honorarios sobre la deuda</td>
                                <td className="p-4 text-emerald-800 font-semibold bg-emerald-50/50">Suscripción fija mensual por copropiedad (desde $350.000 COP / mes)</td>
                              </tr>
                              <tr>
                                <td className="p-4 font-semibold text-slate-900">Inmediatez de Pago</td>
                                <td className="p-4 text-slate-600">Lenta; consignaciones manuales y recibos</td>
                                <td className="p-4 text-emerald-800 font-semibold bg-emerald-50/50">Inmediata con botón PSE directo en WhatsApp</td>
                              </tr>
                              <tr>
                                <td className="p-4 font-semibold text-slate-900">Riesgo Sanción SIC (Ley 2300)</td>
                                <td className="p-4 text-rose-600 font-medium">Alto por llamadas o mensajes a deshoras</td>
                                <td className="p-4 text-emerald-800 font-semibold bg-emerald-50/50">0% riesgo; control de horarios y bitácora legal</td>
                              </tr>
                              <tr>
                                <td className="p-4 font-semibold text-slate-900">Relación Vecinal</td>
                                <td className="p-4 text-slate-600">Desgaste y discusiones en asamblea</td>
                                <td className="p-4 text-emerald-800 font-semibold bg-emerald-50/50">Comunicación institucional y respetuosa</td>
                              </tr>
                              <tr>
                                <td className="p-4 font-semibold text-slate-900">Preparación de Demanda</td>
                                <td className="p-4 text-slate-600">Semanas calculando intereses y certificaciones</td>
                                <td className="p-4 text-emerald-800 font-semibold bg-emerald-50/50">1 Clic para descargar Título Ejecutivo en PDF</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>

                    {/* ── PLANES DE PRECIOS (LIGHT) ── */}
                    <section id="precios" className="py-20 border-b border-slate-200/80 bg-white">
                      <div className="container mx-auto px-4 max-w-6xl">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                          <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-3 uppercase tracking-wider text-xs font-semibold">
                            Inversión Transparente
                          </Badge>
                          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
                            Planes Adaptados al Tamaño de tu Conjunto
                          </h2>
                          <p className="text-slate-600 mt-3 text-base">
                            Tarifa fija mensual en pesos colombianos por copropiedad, sin comisiones sobre el capital recuperado.
                          </p>
                        </div>

                        {/* Selector Interactivo: Número de Copropiedades Administradas */}
                        <div className="max-w-2xl mx-auto mb-12 p-4 md:p-5 rounded-2xl border border-slate-200 bg-slate-50/90 shadow-xs">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                            <div>
                              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">¿Administras varias copropiedades?</div>
                              <div className="text-sm font-bold text-slate-900">Selecciona cuántas copropiedades gestionas:</div>
                            </div>
                            <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full border border-emerald-200 shrink-0">
                              <Building2 className="h-3.5 w-3.5" />
                              {copropiedadesPlan === 1 ? "1 Copropiedad" : `${copropiedadesPlan} Copropiedades`}
                              {planPrices.discountPercent > 0 && ` (-${planPrices.discountPercent}% dto)`}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                              { count: 1, label: "1 Copropiedad", sub: "Tarifa estándar" },
                              { count: 2, label: "2 a 3 PHs", sub: "15% dto / copropiedad" },
                              { count: 5, label: "4 a 6 PHs", sub: "25% dto / copropiedad" },
                              { count: 8, label: "7+ PHs", sub: "35% dto / copropiedad" }
                            ].map((tier) => {
                              const isSelected =
                                (tier.count === 1 && copropiedadesPlan === 1) ||
                                (tier.count === 2 && copropiedadesPlan >= 2 && copropiedadesPlan <= 3) ||
                                (tier.count === 5 && copropiedadesPlan >= 4 && copropiedadesPlan <= 6) ||
                                (tier.count === 8 && copropiedadesPlan >= 7);

                              return (
                                <button
                                  key={tier.count}
                                  type="button"
                                  onClick={() => setCopropiedadesPlan(tier.count)}
                                  className={`p-2.5 rounded-xl border text-left transition-all ${isSelected
                                      ? "bg-white border-emerald-500 shadow-md ring-2 ring-emerald-500/20 text-slate-900"
                                      : "bg-white/70 border-slate-200 hover:bg-white text-slate-600 hover:border-slate-300"
                                    }`}
                                >
                                  <div className="font-bold text-xs">{tier.label}</div>
                                  <div className={`text-[10px] ${isSelected ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                                    {tier.sub}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                          {/* Plan Básico */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                            <div>
                              <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Copropiedad Pequeña</div>
                              <div className="text-xs text-slate-400 mb-4">Hasta 80 Unidades Privadas</div>

                              <div className="text-3xl md:text-4xl font-extrabold text-slate-900 font-mono mb-1">
                                ${planPrices.pequena.pricePerPh.toLocaleString("es-CO")}
                              </div>
                              <div className="text-xs text-slate-600 mb-3 font-medium">
                                COP / mes <strong className="text-emerald-700 font-semibold">por copropiedad</strong>
                              </div>

                              {copropiedadesPlan > 1 && (
                                <div className="mb-4 p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs text-emerald-950">
                                  <div className="flex justify-between items-center font-medium">
                                    <span>Total {copropiedadesPlan} PHs:</span>
                                    <strong className="font-mono">${planPrices.pequena.totalMonthly.toLocaleString("es-CO")} COP</strong>
                                  </div>
                                  <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                                    Ahorras ${planPrices.pequena.totalSavings.toLocaleString("es-CO")} COP/mes (-{planPrices.discountPercent}%)
                                  </div>
                                </div>
                              )}

                              {/* Cupos de Notificaciones */}
                              <div className="space-y-2 mb-6 p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                                <div className="flex items-center justify-between text-xs font-semibold text-emerald-800">
                                  <span className="flex items-center gap-1.5">
                                    <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                                    WhatsApp con Botón PSE:
                                  </span>
                                  <span className="font-mono bg-emerald-100/80 px-2 py-0.5 rounded text-emerald-900 font-bold">250 / mes</span>
                                </div>
                                <div className="flex items-center justify-between text-xs font-medium text-slate-700">
                                  <span className="flex items-center gap-1.5">
                                    <Mail className="h-3.5 w-3.5 text-sky-600" />
                                    Correos Certificados:
                                  </span>
                                  <span className="font-mono bg-slate-200/70 px-2 py-0.5 rounded text-slate-800 font-bold">500 / mes</span>
                                </div>
                                <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-200/60 flex justify-between">
                                  <span>Extra WhatsApp: $400 COP</span>
                                  <span>Extra Email: $350 COP</span>
                                </div>
                              </div>

                              <ul className="space-y-2.5 text-sm text-slate-600">
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Carga masiva desde Excel</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Semáforo de Mora Ley 675</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Título Ejecutivo PDF Art. 48</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Blindaje y Bitácora Ley 2300</span>
                                </li>
                              </ul>
                            </div>

                            <div className="pt-6 mt-6 border-t border-slate-100">
                              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold" asChild>
                                  <a
                                    href={`https://wa.me/573507679981?text=${encodeURIComponent(
                                      `Hola, me interesa contratar el Plan Pequeño (hasta 80 unidades) para ${copropiedadesPlan} copropiedad(es) a un valor de $${planPrices.pequena.pricePerPh.toLocaleString("es-CO")} COP/mes por copropiedad. ¿Podemos coordinar la activación?`
                                    )}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Contratar Plan
                                  </a>
                              </Button>
                            </div>
                          </div>

                          {/* Plan Mediano (Destacado Light) */}
                          <div className="rounded-2xl border-2 border-emerald-500 bg-white p-8 flex flex-col justify-between relative shadow-xl shadow-emerald-600/10">
                            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white font-extrabold text-xs px-4 py-1 rounded-full uppercase tracking-wider shadow-sm">
                              Más Recomendado
                            </div>

                            <div>
                              <div className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-1">Copropiedad Mediana</div>
                              <div className="text-xs text-slate-500 mb-4">81 a 200 Unidades Privadas</div>

                              <div className="text-3xl md:text-4xl font-extrabold text-slate-900 font-mono mb-1">
                                ${planPrices.mediana.pricePerPh.toLocaleString("es-CO")}
                              </div>
                              <div className="text-xs text-slate-600 mb-3 font-medium">
                                COP / mes <strong className="text-emerald-700 font-semibold">por copropiedad</strong>
                              </div>

                              {copropiedadesPlan > 1 && (
                                <div className="mb-4 p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs text-emerald-950">
                                  <div className="flex justify-between items-center font-medium">
                                    <span>Total {copropiedadesPlan} PHs:</span>
                                    <strong className="font-mono">${planPrices.mediana.totalMonthly.toLocaleString("es-CO")} COP</strong>
                                  </div>
                                  <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                                    Ahorras ${planPrices.mediana.totalSavings.toLocaleString("es-CO")} COP/mes (-{planPrices.discountPercent}%)
                                  </div>
                                </div>
                              )}

                              {/* Cupos de Notificaciones */}
                              <div className="space-y-2 mb-6 p-3.5 rounded-xl bg-emerald-50/70 border border-emerald-200">
                                <div className="flex items-center justify-between text-xs font-semibold text-emerald-900">
                                  <span className="flex items-center gap-1.5">
                                    <MessageSquare className="h-3.5 w-3.5 text-emerald-700" />
                                    WhatsApp con Botón PSE:
                                  </span>
                                  <span className="font-mono bg-emerald-200/80 px-2 py-0.5 rounded text-emerald-950 font-bold">600 / mes</span>
                                </div>
                                <div className="flex items-center justify-between text-xs font-medium text-slate-800">
                                  <span className="flex items-center gap-1.5">
                                    <Mail className="h-3.5 w-3.5 text-sky-700" />
                                    Correos Certificados:
                                  </span>
                                  <span className="font-mono bg-emerald-100/70 px-2 py-0.5 rounded text-slate-900 font-bold">1.200 / mes</span>
                                </div>
                                <div className="text-[11px] text-emerald-800/90 pt-1 border-t border-emerald-200 flex justify-between font-medium">
                                  <span>Extra WhatsApp: $400 COP</span>
                                  <span>Extra Email: $350 COP</span>
                                </div>
                              </div>

                              <ul className="space-y-2.5 text-sm text-slate-700">
                                <li className="flex items-center gap-2 font-medium text-emerald-800">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Todo lo del Plan Básico</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Soporte prioritario para el Administrador</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Campañas quincenales automatizadas</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Directorio CRUD de copropietarios</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Informe ejecutivo para el Consejo</span>
                                </li>
                              </ul>
                            </div>

                            <div className="pt-6 mt-6 border-t border-slate-100">
                              <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-md shadow-emerald-600/25" asChild>
                                  <a
                                    href={`https://wa.me/573507679981?text=${encodeURIComponent(
                                      `Hola, me interesa contratar el Plan Mediano (81 a 200 unidades) para ${copropiedadesPlan} copropiedad(es) a un valor de $${planPrices.mediana.pricePerPh.toLocaleString("es-CO")} COP/mes por copropiedad. ¿Podemos coordinar la activación?`
                                    )}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Elegir Plan Mediano
                                  </a>
                              </Button>
                            </div>
                          </div>

                          {/* Plan Grande */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-8 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                            <div>
                              <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Copropiedad Grande</div>
                              <div className="text-xs text-slate-400 mb-4">Más de 200 Unidades Privadas</div>

                              <div className="text-3xl md:text-4xl font-extrabold text-slate-900 font-mono mb-1">
                                ${planPrices.grande.pricePerPh.toLocaleString("es-CO")}
                              </div>
                              <div className="text-xs text-slate-600 mb-3 font-medium">
                                COP / mes <strong className="text-emerald-700 font-semibold">por copropiedad</strong>
                              </div>

                              {copropiedadesPlan > 1 && (
                                <div className="mb-4 p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs text-emerald-950">
                                  <div className="flex justify-between items-center font-medium">
                                    <span>Total {copropiedadesPlan} PHs:</span>
                                    <strong className="font-mono">${planPrices.grande.totalMonthly.toLocaleString("es-CO")} COP</strong>
                                  </div>
                                  <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                                    Ahorras ${planPrices.grande.totalSavings.toLocaleString("es-CO")} COP/mes (-{planPrices.discountPercent}%)
                                  </div>
                                </div>
                              )}

                              {/* Cupos de Notificaciones */}
                              <div className="space-y-2 mb-6 p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                                <div className="flex items-center justify-between text-xs font-semibold text-emerald-800">
                                  <span className="flex items-center gap-1.5">
                                    <MessageSquare className="h-3.5 w-3.5 text-emerald-600" />
                                    WhatsApp con Botón PSE:
                                  </span>
                                  <span className="font-mono bg-emerald-100/80 px-2 py-0.5 rounded text-emerald-900 font-bold">1.500 / mes</span>
                                </div>
                                <div className="flex items-center justify-between text-xs font-medium text-slate-700">
                                  <span className="flex items-center gap-1.5">
                                    <Mail className="h-3.5 w-3.5 text-sky-600" />
                                    Correos Certificados:
                                  </span>
                                  <span className="font-mono bg-slate-200/70 px-2 py-0.5 rounded text-slate-800 font-bold">3.000 / mes</span>
                                </div>
                                <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-200/60 flex justify-between">
                                  <span>Extra WhatsApp: $400 COP</span>
                                  <span>Extra Email: $350 COP</span>
                                </div>
                              </div>

                              <ul className="space-y-2.5 text-sm text-slate-600">
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Unidades ilimitadas en el directorio</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Personalización con logo del conjunto</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Títulos Ejecutivos masivos para juzgados</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Capacitación en vivo para el Consejo</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span>Asesoría procesal en Ley 675</span>
                                </li>
                              </ul>
                            </div>

                            <div className="pt-6 mt-6 border-t border-slate-100">
                              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold" asChild>
                                  <a
                                    href={`https://wa.me/573507679981?text=${encodeURIComponent(
                                      `Hola, me interesa contratar el Plan Grande (más de 200 unidades) para ${copropiedadesPlan} copropiedad(es) a un valor de $${planPrices.grande.pricePerPh.toLocaleString("es-CO")} COP/mes por copropiedad. ¿Podemos coordinar una reunión de presentación?`
                                    )}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Contratar Plan
                                  </a>
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Cuadro de Transparencia de Consumo */}
                        <div className="mt-8 p-4 rounded-xl border border-slate-200 bg-white/80 max-w-4xl mx-auto flex items-start gap-3 shadow-sm">
                          <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700 mt-0.5">
                            <ShieldCheck className="h-5 w-5" />
                          </div>
                          <div className="text-xs text-slate-600 leading-relaxed">
                            <strong className="text-slate-900">Transparencia Operativa:</strong> Los mensajes de WhatsApp se envían mediante la API Oficial de Meta Business con botón verificado de pago PSE / Wompi y control estricto de horarios según la Ley 2300 de 2023. Si tu copropiedad requiere volumen adicional durante campañas extraordinarias, puedes recargar bolsas de saldo en cualquier momento sin vencimiento.
                          </div>
                        </div>

                        {/* Banner Multicopropiedad */}
                        <div className="mt-12 p-6 rounded-2xl border border-slate-200 bg-slate-50 text-center max-w-2xl mx-auto shadow-sm">
                          <div className="text-amber-800 font-bold text-sm mb-1">¿Eres Empresa Administradora o gestionas más de 3 copropiedades?</div>
                          <p className="text-xs text-slate-600 mb-4">
                            Accede a tarifas corporativas desde <strong>$227.500 COP / mes por copropiedad</strong> con panel centralizado multi-PH, soporte prioritario y facturación consolidada.
                          </p>
                          <div className="flex justify-center mt-3">
                            <Button className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs gap-2 shadow-sm" asChild>
                                <a
                                  href={`https://wa.me/573507679981?text=${encodeURIComponent(
                                    "Hola, administro varias copropiedades y deseo una propuesta corporativa Multicopropiedad con tarifas preferenciales por copropiedad. ¿Me pueden asesorar?"
                                  )}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                  Consultar Paquete Multicopropiedad por WhatsApp
                                </a>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ── FAQ (LIGHT) ── */}
                    <section className="py-20 border-b border-slate-200/80 bg-slate-50">
                      <div className="container mx-auto px-4 max-w-4xl">
                        <div className="text-center mb-14">
                          <Badge className="bg-slate-200 text-slate-700 border-slate-300 mb-3 uppercase tracking-wider text-xs font-semibold">
                            Preguntas Frecuentes
                          </Badge>
                          <h2 className="text-3xl font-extrabold text-slate-900">
                            Resolvemos las Dudas del Administrador y del Consejo
                          </h2>
                        </div>

                        <div className="space-y-4">
                          {faqs.map((faq, idx) => (
                            <div
                              key={idx}
                              className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-all"
                            >
                              <button
                                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                className="w-full p-5 text-left flex justify-between items-center gap-4 hover:bg-slate-50/80 transition-colors"
                              >
                                <span className="font-semibold text-slate-900 text-base">{faq.q}</span>
                                <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-emerald-600' : ''}`} />
                              </button>
                              {openFaq === idx && (
                                <div className="p-5 pt-0 text-slate-600 text-sm leading-relaxed border-t border-slate-100">
                                  {faq.a}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>

                    {/* ── CTA FINAL (LIGHT) ── */}
                    <section className="py-20 bg-gradient-to-b from-slate-50 to-white text-center">
                      <div className="container mx-auto px-4 max-w-4xl">
                        <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6">
                          Recupera la Liquidez de tu Conjunto Este Mes
                        </h2>
                        <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto mb-10">
                          Agenda una demostración de 10 minutos con tu archivo de cartera real y descubre cuánto dinero
                          puedes ingresar a la cuenta de la copropiedad antes del próximo corte.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
                          <Button
                            size="lg"
                            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-7 shadow-lg shadow-emerald-600/25 gap-2"
                            asChild
                          >
                            <a href="https://wa.me/573507679981?text=Hola%2C%20soy%20Administrador%28a%29%20de%20Propiedad%20Horizontal%20y%20quiero%20agendar%20una%20demostraci%C3%B3n%20de%2010%20minutos%20de%20MiA%20Cartera%20PH%20con%20la%20cartera%20de%20mi%20conjunto." target="_blank" rel="noreferrer">
                              <MessageSquare className="h-5 w-5" />
                              Escribir por WhatsApp al 350 767 9981
                            </a>
                          </Button>

                          <Button
                            size="lg"
                            variant="outline"
                            className="w-full sm:w-auto border-emerald-600/40 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-semibold shadow-xs gap-2"
                            asChild
                          >
                            <Link to="/brochure-cartera-ph">
                              <FileText className="h-4 w-4 text-emerald-700" />
                              Ver Brochure Comercial
                            </Link>
                          </Button>

                          <Button
                            size="lg"
                            variant="outline"
                            className="w-full sm:w-auto border-slate-300 bg-white hover:bg-slate-100 text-slate-700 shadow-xs gap-2"
                            onClick={handleDownloadBrochure}
                            disabled={isDownloadingBrochure}
                          >
                            <Download className="h-4 w-4 text-slate-600" />
                            {isDownloadingBrochure ? "Descargando..." : "Descargar Folleto PDF"}
                          </Button>
                        </div>
                      </div>
                    </section>
                  </main>

                  <Footer />
                </div>
                );
}
